import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import TaskRow from "@/components/TaskRow";
import TaskDialog from "@/components/TaskDialog";
import TaskEditDialog from "@/components/TaskEditDialog";
import { ArrowLeft, Plus, User, FolderKanban, ArrowRight, Users2 } from "lucide-react";
import { toast } from "sonner";

export default function CollaboratorDetail() {
  const { id } = useParams();
  const [collab, setCollab] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [dlg, setDlg] = useState({ open: false, direction: "to_me" });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [c, t, p, allC] = await Promise.all([
      api.get(`/collaborators/${id}`),
      api.get("/tasks", { params: { collaborator_id: id } }),
      api.get("/projects", { params: { collaborator_id: id } }),
      api.get("/collaborators"),
    ]);
    setCollab(c.data); setTasks(t.data); setProjects(p.data); setCollabs(allC.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const collabById = useMemo(() => Object.fromEntries(collabs.map((c) => [c.id, c])), [collabs]);

  const ownerTasks = useMemo(() => tasks.filter((t) => !t.from_collaborator_id && t.collaborator_id === id), [tasks, id]);
  const toMe = useMemo(() => ownerTasks.filter((t) => t.direction === "to_me"), [ownerTasks]);
  const toThem = useMemo(() => ownerTasks.filter((t) => t.direction === "to_them"), [ownerTasks]);

  const crossTasks = useMemo(() => tasks.filter((t) => !!t.from_collaborator_id), [tasks]);
  const owesOthers = useMemo(() => crossTasks.filter((t) => t.from_collaborator_id === id), [crossTasks, id]);
  const owedByOthers = useMemo(() => crossTasks.filter((t) => t.collaborator_id === id), [crossTasks, id]);

  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" });
    load();
  };
  const del = async (t) => {
    if (!window.confirm("Obrisati task?")) return;
    await api.delete(`/tasks/${t.id}`);
    toast.success("Obrisano");
    load();
  };

  if (!collab) return <div className="text-ink-muted">Učitavanje...</div>;

  return (
    <div className="space-y-8">
      <Link to="/saradnici" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-forest" data-testid="back-to-saradnici">
        <ArrowLeft className="w-4 h-4" /> Nazad na saradnike
      </Link>

      <div className="flex flex-col md:flex-row md:items-center gap-5">
        {collab.avatar_url ? (
          <img src={collab.avatar_url} alt={collab.name} className="w-20 h-20 rounded-lg object-cover border border-line" />
        ) : (
          <div className="w-20 h-20 rounded-lg bg-surface-secondary border border-line grid place-items-center text-ink-muted">
            <User className="w-8 h-8" />
          </div>
        )}
        <div className="flex-1">
          <div className="overline">Saradnik</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-heading font-semibold tracking-tight" data-testid="collab-detail-name">
            {collab.name}
          </h1>
          <div className="mt-1 text-ink-muted text-sm">{collab.role || collab.email || "—"}</div>
        </div>
      </div>

      {projects.length > 0 && (
        <div>
          <div className="overline mb-2">Zajednički projekti</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {projects.map((p) => (
              <Link key={p.id} to={`/projekti/${p.id}`} className="surface-card p-4 hover:bg-surface-secondary" data-testid={`collab-project-${p.id}`}>
                <div className="flex items-center gap-2 text-forest">
                  <FolderKanban className="w-4 h-4" /> <span className="font-heading font-medium">{p.name}</span>
                </div>
                <div className="text-xs text-ink-muted mt-1 line-clamp-2">{p.description}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Column
          title="On duguje meni"
          subtitle={`${collab.name} treba da završi za tebe`}
          tone="#2C402E"
          tasks={toMe}
          onNew={() => setDlg({ open: true, direction: "to_me" })}
          onToggle={toggle}
          onOpen={setEditing}
          onDelete={del}
          testid="col-to-me"
        />
        <Column
          title="Ja dugujem njemu"
          subtitle={`Ti treba da završiš za ${collab.name}`}
          tone="#C65D47"
          tasks={toThem}
          onNew={() => setDlg({ open: true, direction: "to_them" })}
          onToggle={toggle}
          onOpen={setEditing}
          onDelete={del}
          testid="col-to-them"
        />
      </div>

      {(owesOthers.length > 0 || owedByOthers.length > 0) && (
        <div>
          <div className="overline flex items-center gap-1.5 mb-3"><Users2 className="w-3 h-3" /> Sa drugim saradnicima</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CrossColumn
              title={`${collab.name} duguje drugima`}
              tone="#D99A5B"
              tasks={owesOthers}
              collabById={collabById}
              onToggle={toggle}
              onOpen={setEditing}
              testid="col-owes-others"
            />
            <CrossColumn
              title={`Drugi duguju ${collab.name}`}
              tone="#82937E"
              tasks={owedByOthers}
              collabById={collabById}
              onToggle={toggle}
              onOpen={setEditing}
              testid="col-owed-by-others"
            />
          </div>
        </div>
      )}

      <TaskDialog
        open={dlg.open}
        onOpenChange={(v) => setDlg((d) => ({ ...d, open: v }))}
        collaborators={collabs}
        projects={projects}
        defaultCollaboratorId={id}
        defaultDirection={dlg.direction}
        onCreated={load}
      />

      {editing && (
        <TaskEditDialog
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          task={editing}
          collaborators={collabs}
          onChanged={load}
          onDeleted={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Column({ title, subtitle, tone, tasks, onNew, onToggle, onOpen, onDelete, testid }) {
  return (
    <div data-testid={testid} className="surface-card">
      <div className="p-5 border-b border-line flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: tone }} />
            <div className="font-heading font-semibold text-lg">{title}</div>
            <div className="text-xs text-ink-muted">({tasks.length})</div>
          </div>
          <div className="text-xs text-ink-muted mt-0.5">{subtitle}</div>
        </div>
        {onNew && (
          <button className="btn-ghost !py-1.5 !px-3 text-xs" onClick={onNew} data-testid={`${testid}-add`}>
            <Plus className="w-3.5 h-3.5" /> Dodaj
          </button>
        )}
      </div>
      <div>
        {tasks.length === 0 ? (
          <div className="p-6 text-sm text-ink-muted">Nema stavki.</div>
        ) : (
          tasks.map((t) => (
            <div key={t.id} className="flex items-stretch border-b border-line last:border-b-0">
              <button className="pl-3 pt-3" onClick={() => onToggle(t)}>
                <div className={`w-5 h-5 rounded-full border-2 ${t.status === "done" ? "bg-forest border-forest" : "border-ink-muted"}`} />
              </button>
              <button className="flex-1 text-left px-3 py-3 hover:bg-surface-secondary transition-colors" onClick={() => onOpen(t)} data-testid={`task-open-${t.id}`}>
                <div className={`text-sm font-medium ${t.status === "done" ? "line-through text-ink-muted" : "text-ink"}`}>{t.title}</div>
                <div className="text-xs text-ink-muted mt-0.5 flex flex-wrap items-center gap-2">
                  {t.due_date && <span>{t.due_date}</span>}
                  <span className="capitalize">· {t.priority}</span>
                </div>
              </button>
              <button onClick={() => onDelete(t)} className="px-3 text-ink-muted hover:text-terracotta text-xs" title="Obriši">×</button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function CrossColumn({ title, tone, tasks, collabById, onToggle, onOpen, testid }) {
  return (
    <div data-testid={testid} className="surface-card">
      <div className="p-5 border-b border-line flex items-center gap-2">
        <div className="w-2 h-2 rounded-full" style={{ background: tone }} />
        <div className="font-heading font-semibold">{title}</div>
        <div className="text-xs text-ink-muted">({tasks.length})</div>
      </div>
      <div>
        {tasks.length === 0 ? (
          <div className="p-6 text-sm text-ink-muted">Nema stavki.</div>
        ) : (
          tasks.map((t) => {
            const from = collabById[t.from_collaborator_id];
            const to = collabById[t.collaborator_id];
            return (
              <div key={t.id} className="flex items-stretch border-b border-line last:border-b-0">
                <button className="pl-3 pt-3" onClick={() => onToggle(t)}>
                  <div className={`w-5 h-5 rounded-full border-2 ${t.status === "done" ? "bg-forest border-forest" : "border-ink-muted"}`} />
                </button>
                <button className="flex-1 text-left px-3 py-3 hover:bg-surface-secondary transition-colors" onClick={() => onOpen(t)} data-testid={`task-open-${t.id}`}>
                  <div className={`text-sm font-medium ${t.status === "done" ? "line-through text-ink-muted" : "text-ink"}`}>{t.title}</div>
                  <div className="text-xs text-ink-muted mt-1 flex flex-wrap items-center gap-1">
                    <span className="text-forest font-medium">{from?.name || "—"}</span>
                    <ArrowRight className="w-3 h-3" />
                    <span className="text-forest font-medium">{to?.name || "—"}</span>
                    {t.due_date && <span className="ml-2">· {t.due_date}</span>}
                    <span className="ml-1 capitalize">· {t.priority}</span>
                  </div>
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
